import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useModal from 'hooks/useModal';
import { ProviderProps, useCallback, useContext, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import createNamedContext from 'utils/createNamedContext';
import ScenarioModal, { ModalMeta } from './ScenarioModal';

export function useGetScenarioText() {
  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const botReplyTypeLabel = useMemo<
    Omit<
      Record<Conversation.BotReplyType, string>,
      'BRT_UNKNOWN' | 'MARKETING' | 'LOFA_MARKETING' | 'RECEPTION_SCORE'
    >
  >(() => {
    return {
      DIALOGUE: t('domain.aiReception.type.aiReception'),
      WELCOME: t('domain.aiReception.type.storeWelcomeMessage'),
      TIMEOUT_AUTO_REPLY: t('domain.aiReception.type.autoReply'),
      GUARANTEED_ANSWER:
        (
          {
            'zh-CN': 'AI值守兜底',
            'en-US': 'AI On-duty reply',
          } as Partial<Record<General.SystemLanguage, string>>
        )[systemLang] ||
        `${t('domain.aiReception.type.aiReception')}-${t(
          'domain.aiReception.type.onDutyAutoReply',
        )}`,
      COMPENSATORY_REPLY:
        (
          {
            'zh-CN': ' AI结束语',
            'en-US': 'Closing words by AI',
          } as Partial<Record<General.SystemLanguage, string>>
        )[systemLang] ||
        `${t('domain.aiReception.type.aiReception')}-${t(
          'domain.aiReception.type.closingMessage',
        )}`,
      TO_BUYER_AUTO_REPLY: t('domain.aiReception.type.autoReplyToUselessMessages'),
      TO_BLOCK_BUYER: t('domain.aiReception.type.blacklistAutoReply'),
      LLM_AGENT: t('domain.aiReception.type.aiReceptionPro'),
    };
  }, [systemLang, t]);

  const botMarketingTypeLabel = useMemo<
    Omit<Record<Conversation.BotMarketingType, string>, 'BMT_UNKNOWN' | 'BMT_TIKTOK_END_SESSION'>
  >(() => {
    return {
      BMT_INVITE_FOLLOWER: t('domain.aiReception.type.autoInviteFollow'),
      BMT_ORDER_REMINDER: t('domain.aiReception.type.orderReminder'),
      BMT_PAYMENT_REMINDER: t('scene.chatWorkspace.aiReception.action.paymentReminder'),
      BMT_PRAISE_REMINDER: t('scene.chatWorkspace.aiReception.action.reviewInvitation'),
      BMT_BAD_REVIEW_RECOVERY: t('domain.aiReception.type.negativeReviewRecovery'),
      BMT_CANCEL_ORDER_REMINDER: t('domain.aiReception.type.buyerCancelOrderRecovery'),
      BMT_SYSTEM_CANCEL_ORDER: t('domain.aiReception.type.systemCancelOrderRecovery'),
      BMT_RETURN_ORDER_REMINDER: t('domain.aiReception.type.returnRefundRecovery'),
      BMT_ONLY_RETURN_ORDER_REMINDER: t('domain.aiReception.type.refundRecovery'),
      BMT_RETURN_TRADE_REMINDER: t('domain.aiReception.type.refundOnlyOrReturnRefund'),
      BMT_OUT_STOCK_REMINDER: t('domain.aiReception.type.outOfStockReminder'),
      BMT_CONFIRM_ORDER_REMINDER: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.orderConfirmed',
      )}`,
      BMT_DELAY_SHIP: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.delayedShipment',
      )}`,
      BMT_SHIPPED_REMINDER: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.shipped',
      )}`,
      BMT_WAIT_FOR_TAX: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.waitForTaxPayment',
      )}`,
      BMT_DELIVERY_REMINDER: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.lastMileDelivery',
      )}`,
      BMT_WAIT_DELIVERY_AGAIN: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.waitingForRedelivery',
      )}`,
      BMT_SIGN_REMINDER: `${t('domain.aiReception.type.logisticsCare')}-${t(
        'domain.aiReception.type.received',
      )}`,
      BMT_GOOD_REVIEW_REMINDER: t('domain.aiReception.type.positiveReviewFollowUp'),
      BMT_SIGNAL_COUPON: t('domain.aiReception.type.passphraseToClaimCode'),
      BMT_ORDER_FOLLOWUP: t('domain.aiReception.type.problemOrderFollowUp'),
      BMT_INITIATIVE_TASK: `${t('domain.aiReception.type.promotionBot')}-${t(
        'scene.chatWorkspace.aiReception.scenarioPreview.action.contactBuyer',
      )}`,
    };
  }, [t]);

  const getScenarioText = useCallback(
    (botReplyType: Conversation.BotReplyType, extraInfo?: Conversation.ExtraInfo) => {
      if (botReplyType === 'BRT_UNKNOWN') {
        return undefined;
      }

      const { botMarketingType, replyOption, actionType } = extraInfo || {};

      if (botReplyType === 'MARKETING') {
        if (!botMarketingType || botMarketingType === 'BMT_UNKNOWN') {
          return undefined;
        }

        if (botMarketingType === 'BMT_TIKTOK_END_SESSION') {
          return actionType === 'BUYER_CARE' ? t('买家关怀') : t('邀好评');
        }

        return botMarketingTypeLabel[botMarketingType];
      }

      if (botReplyType === 'RECEPTION_SCORE') {
        if (replyOption) {
          return t('domain.aiReception.type.replyToCustomerRating');
        }

        return t('domain.aiReception.type.receptionRatingInvitation');
      }

      return botReplyTypeLabel[botReplyType];
    },
    [botReplyTypeLabel, botMarketingTypeLabel, t],
  );

  return {
    t,
    botReplyTypeLabel,
    botMarketingTypeLabel,
    getScenarioText,
  };
}

export function useScenarioContextValue() {
  const modal = useModal<ModalMeta>();

  return {
    modal,
    ...useGetScenarioText(),
  };
}

export type ScenarioContextValue = ReturnType<typeof useScenarioContextValue> | null;

export const ScenarioContext = createNamedContext<ScenarioContextValue>('ScenarioContext', null);

export const useScenarioContext = () => {
  const value = useContext(ScenarioContext);

  if (value === null) {
    throw new Error(
      'useScenarioContext hook should be used in a context of ScenarioProvider component',
    );
  }

  return value;
};

export function ScenarioProvider(props: Omit<ProviderProps<ScenarioContextValue>, 'value'>) {
  const { children, ...otherProps } = props;

  const value = useScenarioContextValue();

  const {
    modal: { visible, hide, meta },
  } = value;

  return (
    <ScenarioContext.Provider value={value} {...otherProps}>
      {children}
      {meta && <ScenarioModal {...meta} visible={visible} onCancel={() => hide()} />}
    </ScenarioContext.Provider>
  );
}

// 获取下发场景文本
export function useGetBotReplyTypeText() {
  const { getScenarioText } = useGetScenarioText();

  return (botReplyType: Conversation.BotReplyType, extra: string) => {
    try {
      const extraInfo = JSON.parse(extra);

      return getScenarioText(botReplyType, extraInfo) || '-';
    } catch (err) {
      return '-';
    }
  };
}
